// The single seam the messaging components talk through, backed by the
// generated URQL hooks. New messages arrive in real time via the messageSent
// subscription (websocket), which triggers network-only refetches so the
// requester-relative fields (unreadCount etc.) stay server-computed. Polling
// remains as a slow fallback for when the websocket is down.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useClient } from "urql";
import {
  GetMessagesDocument,
  GetMessagesQuery,
  GetMessagesQueryVariables,
  useGetConversationsQuery,
  useGetMessageableCounterpartsQuery,
  useGetMessagesQuery,
  useMarkConversationReadMutation,
  useMeQuery,
  useMessageSentSubscription,
  useSendMessageMutation,
  useSetConversationArchivedMutation,
  useSetConversationStarredMutation,
  useStartConversationMutation,
} from "generated/graphql";
import { useS3 } from "hooks/useS3";
import { useSnack } from "hooks/useSnack";

import {
  ATTACHMENT_FOLDER,
  CONVERSATIONS_POLL_MS,
  MESSAGES_PAGE_SIZE,
  MESSAGES_POLL_MS,
} from "./constants";
import {
  ConversationView,
  MessageView,
  MessageableCounterpart,
  MessagesActionResult,
  SenderSide,
} from "./types";
import { accountRole } from "pages/dashboard/dashboardFeatures";

export const useMySide = (): SenderSide => {
  const [{ data }] = useMeQuery();
  return accountRole(data?.me);
};

type Reexecute = (opts?: { requestPolicy?: "network-only" }) => void;

const usePoll = (reexecute: Reexecute, intervalMs: number, enabled = true) => {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") {
        reexecute({ requestPolicy: "network-only" });
      }
    }, intervalMs);
    return () => clearInterval(timer);
  }, [reexecute, intervalMs, enabled]);
};

export const useConversations = (): {
  conversations: ConversationView[];
  loading: boolean;
  refresh: () => void;
} => {
  const [{ data, fetching }, reexecute] = useGetConversationsQuery();
  usePoll(reexecute, CONVERSATIONS_POLL_MS);

  // Paused while logged out — an unauthenticated socket would be rejected
  // (close 4403) and graphql-ws would keep retrying.
  const [{ data: me }] = useMeQuery();
  const [{ data: event }] = useMessageSentSubscription({ pause: !me?.me });
  useEffect(() => {
    if (event?.messageSent) {
      reexecute({ requestPolicy: "network-only" });
    }
  }, [event, reexecute]);

  return {
    conversations: data?.getConversations ?? [],
    // Initial load only — polling re-executions shouldn't flash a spinner.
    loading: fetching && !data,
    refresh: useCallback(
      () => reexecute({ requestPolicy: "network-only" }),
      [reexecute],
    ),
  };
};

export const useMessages = (
  conversationId: number | null,
): {
  messages: MessageView[];
  loading: boolean;
  hasMore: boolean;
  loadOlder: () => void;
  refresh: () => void;
} => {
  const client = useClient();

  // The polled query always fetches the latest page; older pages are
  // cursor-fetched on demand and merged in front (deduped by id).
  const [{ data, fetching }, reexecute] = useGetMessagesQuery({
    variables: {
      conversationId: conversationId ?? 0,
      limit: MESSAGES_PAGE_SIZE,
    },
    pause: conversationId == null,
    // Serve the cached page instantly but always fetch fresh on open, so a
    // just-arrived message isn't stuck behind stale cache until the poll.
    requestPolicy: "cache-and-network",
  });
  usePoll(reexecute, MESSAGES_POLL_MS, conversationId != null);

  const [{ data: event }] = useMessageSentSubscription({
    pause: conversationId == null,
  });
  useEffect(() => {
    if (event?.messageSent.conversationId === conversationId) {
      reexecute({ requestPolicy: "network-only" });
    }
  }, [event, conversationId, reexecute]);

  const [olderMessages, setOlderMessages] = useState<MessageView[]>([]);
  const [olderHasMore, setOlderHasMore] = useState<boolean | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);

  useEffect(() => {
    setOlderMessages([]);
    setOlderHasMore(null);
  }, [conversationId]);

  const latest = useMemo(() => data?.getMessages.messages ?? [], [data]);

  const messages = useMemo(() => {
    const latestIds = new Set(latest.map((m) => m.id));
    return [...olderMessages.filter((m) => !latestIds.has(m.id)), ...latest];
  }, [latest, olderMessages]);

  const loadOlder = useCallback(async () => {
    const oldestId = messages[0]?.id;
    if (conversationId == null || !oldestId || loadingOlder) {
      return;
    }
    setLoadingOlder(true);
    const res = await client
      .query<GetMessagesQuery, GetMessagesQueryVariables>(
        GetMessagesDocument,
        {
          conversationId,
          beforeId: oldestId,
          limit: MESSAGES_PAGE_SIZE,
        },
        { requestPolicy: "network-only" },
      )
      .toPromise();
    setLoadingOlder(false);

    const page = res.data?.getMessages;
    if (page) {
      setOlderMessages((prev) => [...page.messages, ...prev]);
      setOlderHasMore(page.hasMore);
    }
  }, [client, conversationId, messages, loadingOlder]);

  return {
    messages,
    loading: (fetching && !data) || loadingOlder,
    hasMore: olderHasMore ?? data?.getMessages.hasMore ?? false,
    loadOlder,
    refresh: useCallback(
      () => reexecute({ requestPolicy: "network-only" }),
      [reexecute],
    ),
  };
};

export const useMessageableCounterparts = (): {
  counterparts: MessageableCounterpart[];
  loading: boolean;
} => {
  const [{ data, fetching }] = useGetMessageableCounterpartsQuery({
    requestPolicy: "cache-and-network",
  });

  return {
    counterparts: data?.getMessageableCounterparts ?? [],
    loading: fetching && !data,
  };
};

export const useMessageActions = () => {
  const { uploadToS3 } = useS3();
  const { t } = useTranslation("messages");
  const [, sendMut] = useSendMessageMutation();
  const [, markReadMut] = useMarkConversationReadMutation();
  const [, starMut] = useSetConversationStarredMutation();
  const [, archiveMut] = useSetConversationArchivedMutation();
  const [, startMut] = useStartConversationMutation();

  const send = useCallback(
    async (
      conversationId: number,
      body: string,
      files: File[],
    ): Promise<MessagesActionResult> => {
      let attachments: { name: string; url: string }[];
      try {
        attachments = await Promise.all(
          files.map(async (file) => {
            const url = await uploadToS3(file, ATTACHMENT_FOLDER);
            if (!url) {
              throw new Error(t("snack.upload_failed", { file: file.name }));
            }
            return { name: file.name, url };
          }),
        );
      } catch (err) {
        return {
          ok: false,
          error:
            err instanceof Error ? err.message : t("snack.upload_files_failed"),
        };
      }

      const res = await sendMut({
        options: { conversationId, body, attachments },
      });
      const payload = res.data?.sendMessage;
      if (res.error || (payload?.errors && payload.errors.length > 0)) {
        return {
          ok: false,
          error:
            payload?.errors?.[0]?.message ??
            res.error?.message ??
            t("snack.send_failed"),
        };
      }
      return { ok: true };
    },
    [sendMut, uploadToS3, t],
  );

  const markRead = useCallback(
    async (conversationId: number): Promise<void> => {
      // Returns the updated ConversationView; graphcache normalization
      // clears the unread badge everywhere without a refetch.
      await markReadMut({ conversationId });
    },
    [markReadMut],
  );

  const setStarred = useCallback(
    async (
      conversationId: number,
      starred: boolean,
    ): Promise<MessagesActionResult> => {
      const res = await starMut({ conversationId, starred });
      if (res.error || !res.data?.setConversationStarred) {
        return {
          ok: false,
          error: res.error?.message ?? t("snack.update_failed"),
        };
      }
      return { ok: true };
    },
    [starMut, t],
  );

  const setArchived = useCallback(
    async (
      conversationId: number,
      archived: boolean,
    ): Promise<MessagesActionResult> => {
      const res = await archiveMut({ conversationId, archived });
      if (res.error || !res.data?.setConversationArchived) {
        return {
          ok: false,
          error: res.error?.message ?? t("snack.update_failed"),
        };
      }
      return { ok: true };
    },
    [archiveMut, t],
  );

  const start = useCallback(
    async (
      otherPartyId: number,
    ): Promise<MessagesActionResult & { conversationId?: number }> => {
      const res = await startMut({ otherPartyId });
      const payload = res.data?.startConversation;
      if (
        res.error ||
        (payload?.errors && payload.errors.length > 0) ||
        !payload?.conversation
      ) {
        return {
          ok: false,
          error:
            payload?.errors?.[0]?.message ??
            res.error?.message ??
            t("snack.start_failed"),
        };
      }
      return { ok: true, conversationId: payload.conversation.id };
    },
    [startMut, t],
  );

  return { send, markRead, setStarred, setArchived, start };
};

// "Message this vendor/customer" from anywhere in the dashboard: opens (or
// starts) the conversation and jumps to the messages page.
export const useMessageNavigation = () => {
  const { t } = useTranslation("messages");
  const navigate = useNavigate();
  const showSnack = useSnack();
  const { start } = useMessageActions();

  return useCallback(
    async (otherPartyId: number) => {
      const result = await start(otherPartyId);
      if (!result.ok || !result.conversationId) {
        showSnack(result.error ?? t("snack.start_failed"), "error");
        return;
      }
      navigate(`/dashboard/messages?c=${result.conversationId}`);
    },
    [start, navigate, showSnack, t],
  );
};
